import React, { useContext, useMemo } from 'react';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from '../../../components/bootstrap/Card';
import Icon from '../../../components/icon/Icon';
import AuthContext from '../../../contexts/authContext';
import useDarkMode from '../../../hooks/useDarkMode';

const AVATAR_GRADIENTS = [
  ['#4f46e5', '#7c3aed'],
  ['#0ea5e9', '#0284c7'],
  ['#10b981', '#059669'],
  ['#f59e0b', '#d97706'],
  ['#ef4444', '#dc2626'],
  ['#8b5cf6', '#7c3aed'],
  ['#ec4899', '#db2777'],
  ['#06b6d4', '#0891b2'],
];

const getAvatarGradient = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const [from, to] = AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
  return `linear-gradient(135deg, ${from}, ${to})`;
};

const getInitials = (name: string, surname: string): string => {
  const first = name ? name.charAt(0).toUpperCase() : '';
  const last = surname ? surname.charAt(0).toUpperCase() : '';
  return first + last || '?';
};

const formatRole = (roles: string[] | undefined): string => {
  if (!roles || roles.length === 0) return 'User';
  const priority = ['ADMIN', 'PUBLISHER', 'EDITOR', 'AUTHOR'];
  for (const p of priority) {
    if (roles.includes(p)) return p.charAt(0) + p.slice(1).toLowerCase();
  }
  return roles[0].charAt(0) + roles[0].slice(1).toLowerCase();
};

interface IDetailRowProps {
  icon: string;
  label: string;
  value: string | undefined;
}

const DetailRow = ({ icon, label, value }: IDetailRowProps) => (
  <div className='d-flex align-items-center gap-3 py-3' style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
    <span
      style={{
        width: 38,
        height: 38,
        borderRadius: 10,
        background: 'rgba(79,70,229,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
      <Icon icon={icon as Parameters<typeof Icon>[0]['icon']} color='primary' />
    </span>
    <div>
      <div className='text-muted' style={{ fontSize: '0.72rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div className='fw-semibold' style={{ fontSize: '0.9rem' }}>
        {value || <span className='text-muted fst-italic'>—</span>}
      </div>
    </div>
  </div>
);

/**
 * MyProfilePage — displays the profile details of the currently authenticated admin user.
 * Read-only view; password changes are done via the header Change Password modal.
 */
const MyProfilePage = () => {
  const { userData } = useContext(AuthContext);
  const { darkModeStatus } = useDarkMode();

  const displayName = useMemo(() => {
    if (userData?.name && userData?.surname) return `${userData.name} ${userData.surname}`;
    return userData?.name || userData?.email || 'User';
  }, [userData]);

  const initials = useMemo(
    () => getInitials(userData?.name || '', userData?.surname || ''),
    [userData],
  );
  const avatarGradient = useMemo(() => getAvatarGradient(displayName), [displayName]);
  const role = useMemo(() => formatRole(userData?.roles), [userData]);
  const allRoles = userData?.roles?.map((r) => r.charAt(0) + r.slice(1).toLowerCase()).join(', ') || 'User';

  return (
    <PageWrapper title='My Profile'>
      <Page>
        <div className='row justify-content-center'>
          <div className='col-xxl-8 col-xl-10 col-lg-12'>

            {/* Hero Card */}
            <Card className='mb-4 overflow-hidden border-0 shadow-sm'>
              {/* Gradient banner */}
              <div
                style={{
                  height: 120,
                  background: avatarGradient,
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage:
                      'radial-gradient(circle at 80% 50%, rgba(255,255,255,0.15) 0%, transparent 60%)',
                  }}
                />
              </div>

              <CardBody>
                <div className='d-flex align-items-end gap-4' style={{ marginTop: -40 }}>
                  {/* Avatar */}
                  <span
                    className='d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
                    style={{
                      width: 80,
                      height: 80,
                      background: avatarGradient,
                      border: '4px solid #fff',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                      fontSize: '1.6rem',
                      fontWeight: 700,
                      color: '#fff',
                      letterSpacing: '0.04em',
                    }}>
                    {initials}
                  </span>

                  {/* Name + badges */}
                  <div className='flex-grow-1 pb-1'>
                    <h4 className='fw-bold mb-1'>{displayName}</h4>
                    <div className='d-flex flex-wrap gap-2 align-items-center'>
                      <span
                        className='badge'
                        style={{
                          background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                          color: '#fff',
                          fontSize: '0.72rem',
                          padding: '4px 10px',
                          borderRadius: 20,
                          fontWeight: 500,
                        }}>
                        {role}
                      </span>
                      {userData?.position && (
                        <span className='text-muted' style={{ fontSize: '0.85rem' }}>
                          {userData.position}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Account Details Card */}
            <Card className='border-0 shadow-sm'>
              <CardHeader>
                <CardLabel>
                  <CardTitle className='d-flex align-items-center gap-2'>
                    <span
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      <Icon icon='Person' color='light' size='sm' />
                    </span>
                    Account Details
                  </CardTitle>
                </CardLabel>
              </CardHeader>
              <CardBody>
                <DetailRow icon='Badge' label='Full Name' value={displayName} />
                <DetailRow icon='Email' label='Email Address' value={userData?.email} />
                <DetailRow icon='Work' label='Position' value={userData?.position} />
                <DetailRow
                  icon='AdminPanelSettings'
                  label='Assigned Roles'
                  value={allRoles}
                />
                <div className='d-flex align-items-center gap-3 pt-3'>
                  <span
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 10,
                      background: 'rgba(79,70,229,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                    <Icon icon='Security' color='primary' />
                  </span>
                  <div>
                    <div className='text-muted' style={{ fontSize: '0.72rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Password
                    </div>
                    <div className='text-muted fst-italic' style={{ fontSize: '0.85rem' }}>
                      Use "Change Password" from the profile menu to update your password.
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>

          </div>
        </div>
      </Page>
    </PageWrapper>
  );
};

export default MyProfilePage;
